import { Fragment, type ReactNode } from "react";
import { Text } from "../../../../../components/common/typography";

// A small markdown-ish renderer. Runs on every stream delta, so it stays
// regex-based and intentionally narrow: paragraphs, unordered and ordered
// lists, `code`, **bold**, *italic*, and [text](url) links. Anything else
// renders as plain text.

const INLINE_RE =
  /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g;

function renderInline(source: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((match = INLINE_RE.exec(source)) !== null) {
    if (match.index > last) out.push(source.slice(last, match.index));
    if (match[1]) out.push(<strong key={out.length} className="font-bold">{match[2]}</strong>);
    else if (match[3]) out.push(<em key={out.length} className="italic">{match[4]}</em>);
    else if (match[5]) out.push(
      <code key={out.length} className="rounded-[6px] bg-bgLight px-1.5 py-[1px] text-bodyXs">
        {match[6]}
      </code>,
    );
    else if (match[7]) out.push(
      <a
        key={out.length}
        href={match[9]}
        target="_blank"
        rel="noreferrer noopener"
        className="text-accentCyan700 underline underline-offset-2 hover:text-accentCyan"
      >
        {match[8]}
      </a>,
    );
    last = match.index + match[0].length;
  }
  if (last < source.length) out.push(source.slice(last));
  return out.length === 1 && typeof out[0] === "string" ? out[0] : <>{out}</>;
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

function splitBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let current: Block | null = null;
  const flush = () => { if (current) blocks.push(current); current = null; };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const ordered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet) {
      if (current?.kind !== "ul") { flush(); current = { kind: "ul", items: [] }; }
      current.items.push(bullet[1] ?? "");
      continue;
    }
    if (ordered) {
      if (current?.kind !== "ol") { flush(); current = { kind: "ol", items: [] }; }
      current.items.push(ordered[1] ?? "");
      continue;
    }
    if (current?.kind !== "p") { flush(); current = { kind: "p", lines: [] }; }
    current.lines.push(line);
  }
  flush();
  return blocks;
}

export function ChatMarkdown({ text }: { text: string }) {
  if (!text.trim()) return null;
  const blocks = splitBlocks(text);
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, i) => {
        if (block.kind === "p") {
          return (
            <Text key={i} variant="body" tone="body">
              {block.lines.map((line, j) => (
                <Fragment key={j}>
                  {renderInline(line)}
                  {j < block.lines.length - 1 ? <br /> : null}
                </Fragment>
              ))}
            </Text>
          );
        }
        const Tag = block.kind === "ul" ? "ul" : "ol";
        return (
          <Tag
            key={i}
            className={`flex flex-col gap-1.5 pl-5 ${block.kind === "ul" ? "list-disc" : "list-decimal"}`}
          >
            {block.items.map((item, j) => (
              <li key={j}>
                <Text variant="body" tone="body" inline>
                  {renderInline(item)}
                </Text>
              </li>
            ))}
          </Tag>
        );
      })}
    </div>
  );
}
